import { Button, Group, Modal, Stack, Tabs, Text } from '@mantine/core';
import { IconBriefcase } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { CollaborativeItem, useCollaborativeList, useMembersMethods, account } from '@mygpt/app';

import { NoDataTip } from '@/components/Collaborative/NoDataTip';
const { ContentWrap } = account;
import { LoadingSkeleton } from '@/components/Common/LoadingSkeleton';
import { useGPTStore } from '@/store';

const Collaborators = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'robot' | 'dataset'>('robot');
  const { data, loadList, loading } = useCollaborativeList();
  const { leaveMembers, loading: leaveLoading } = useMembersMethods();
  const { patchUserConfig, userInfo } = useGPTStore((state) => state);
  const [leavdItem, setLeaveItem] = useState<CollaborativeItem | null>(null);

  useEffect(() => {
    loadList({ page: 1, resource_type: activeTab });
    const inviteValue = userInfo?.userconfigs.find((config) => config.key === 'invite_notification');
    if (inviteValue && inviteValue.value === 'True') {
      patchUserConfig({ invite_notification: false });
    }
  }, [activeTab]);

  const handleModalClose = () => {
    setLeaveItem(null);
  };

  const handleConfirm = async () => {
    if (!userInfo || !leavdItem) return;
    if (leaveLoading) return;
    await leaveMembers({
      ai_id: leavdItem.id,
      user_ids: [userInfo.user_id],
      resource_type: activeTab,
    });
    handleModalClose();
    loadList({ page: 1, resource_type: activeTab });
  };
  const renderContent = () => {
    if (loading) {
      return <LoadingSkeleton />;
    }
    if (data.length === 0) {
      if (activeTab === 'robot') {
        return <NoDataTip />;
      } else {
        return <p className="text-center mt-10 text-gray-5 text-lg">{t('access.nodata.dataset_desc')}</p>;
      }
    }
    return (
      <Stack className="max-h-[calc(100vh-180px)] overflow-y-auto overflow-x-hidden">
        {data.map((item, index) => (
          <ContentWrap key={index} className="p-4 hover:scale-[102%] transition-all">
            <Group justify="space-between" wrap="nowrap">
              <Group>
                <Text>{item.name}</Text>
                <Text color="gray.4">({item.user.email})</Text>
              </Group>
              <Group wrap="nowrap">
                <Button variant="outline" color="red" size="sm" onClick={() => setLeaveItem(item)}>
                  {t('collaborative.leave')}
                </Button>
                <NavLink
                  to={activeTab === 'robot' ? `/assist/${item.id}/chat` : `/datasets/${item.id}/data-source/file`}
                >
                  <Button size="sm" leftSection={<IconBriefcase />}>
                    {t('collaborative.assist')}
                  </Button>
                </NavLink>
              </Group>
            </Group>
          </ContentWrap>
        ))}
      </Stack>
    );
  };
  return (
    <div className="h-full">
      <Tabs value={activeTab} onChange={(value) => value && setActiveTab(value as 'robot' | 'dataset')} mb="sm">
        <Tabs.List>
          <Tabs.Tab value="robot">{t('access.tab.robot')}</Tabs.Tab>
          <Tabs.Tab value="dataset">{t('access.tab.dataset')}</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {renderContent()}

      <Modal
        centered
        onClose={handleModalClose}
        size="lg"
        withCloseButton
        opened={!!leavdItem}
        title={t('collaborative.modal.title')}
      >
        <Stack>
          <Text size="sm" fw="bold">
            {t('collaborative.modal.question')}
          </Text>
          <Text size="sm" c="gray.5">
            {t('collaborative.modal.desc')}
          </Text>
          <Group justify="space-between" className="border p-3 rounded-md">
            <Text color="violet.7" fw="bold">
              {leavdItem?.name}
            </Text>
            <Text color="gray.4">{leavdItem?.user.email}</Text>
          </Group>
        </Stack>
        <Group justify="flex-end" mt={30}>
          <Button variant="outline" onClick={handleModalClose}>
            {t('alert_cancel')}
          </Button>
          <Button color="red" onClick={handleConfirm} loading={leaveLoading}>
            {t('collaborative.modal.confirm_btn')}
          </Button>
        </Group>
      </Modal>
    </div>
  );
};

export default Collaborators;
